import React, { useState, useEffect } from 'react';
import { Card, Container, Row, Col, Table, Form, Badge, Spinner, Alert } from 'react-bootstrap';
import apiService from '../services/api';

const UtilizationStats = () => {
    const [selectedCustomer, setSelectedCustomer] = useState('all');
    const [organizations, setOrganizations] = useState([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(true);
    const [organizationError, setOrganizationError] = useState('');
    const [hourlySalesData, setHourlySalesData] = useState([]);
    const [loadingHourlySales, setLoadingHourlySales] = useState(false);
    const [hourlySalesError, setHourlySalesError] = useState('');
    const [hourlyLoginsData, setHourlyLoginsData] = useState([]);
    const [loadingHourlyLogins, setLoadingHourlyLogins] = useState(false);
    const [hourlyLoginsError, setHourlyLoginsError] = useState('');
    const [dailySalesData, setDailySalesData] = useState([]);
    const [loadingDailySales, setLoadingDailySales] = useState(false);
    const [dailySalesError, setDailySalesError] = useState('');
    const [dailyLoginsData, setDailyLoginsData] = useState([]);
    const [loadingDailyLogins, setLoadingDailyLogins] = useState(false);
    const [dailyLoginsError, setDailyLoginsError] = useState('');
    const [inventoryTransfersData, setInventoryTransfersData] = useState([]);
    const [loadingInventoryTransfers, setLoadingInventoryTransfers] = useState(false);
    const [inventoryTransfersError, setInventoryTransfersError] = useState('');
    const [utilizationMetrics, setUtilizationMetrics] = useState(null);
    const [loadingUtilizationMetrics, setLoadingUtilizationMetrics] = useState(false);
    const [utilizationMetricsError, setUtilizationMetricsError] = useState('');

    useEffect(() => {
        loadOrganizations();
    }, []);

    useEffect(() => {
        if (selectedCustomer) {
            loadHourlySalesData(selectedCustomer);
            loadHourlyLoginsData(selectedCustomer);
            loadDailySalesData(selectedCustomer);
            loadDailyLoginsData(selectedCustomer);
            loadInventoryTransfersData(selectedCustomer);
            loadUtilizationMetrics(selectedCustomer);
        }
    }, [selectedCustomer]);

    const loadOrganizations = async () => {
        setLoadingOrganizations(true);
        setOrganizationError('');

        try {
            // Fetch all organizations (use large page size to get all)
            const response = await apiService.getOrganizations(1, 1000, '');
            if (response.success) {
                const orgList = [
                    { id: 'all', name: 'All Organizations' },
                    ...(response.data || []).map(org => ({
                        id: org.id,
                        name: org.name
                    }))
                ];
                setOrganizations(orgList);
            } else {
                setOrganizationError(response.error || 'Failed to load organizations');
            }
        } catch (error) {
            console.error('Error loading organizations:', error);
            setOrganizationError('Failed to load organizations');
        } finally {
            setLoadingOrganizations(false);
        }
    };

    const loadHourlySalesData = async (organizationId) => {
        setLoadingHourlySales(true);
        setHourlySalesError('');

        try {
            const response = await apiService.getHourlySalesVolume(organizationId);
            if (response.success) {
                setHourlySalesData(response.data || []);
                console.log('Hourly sales data loaded:', response.data);
            } else {
                setHourlySalesError(response.error || 'Failed to load hourly sales data');
                setHourlySalesData([]);
            }
        } catch (error) {
            console.error('Error loading hourly sales data:', error);
            setHourlySalesError('Failed to load hourly sales data');
            setHourlySalesData([]);
        } finally {
            setLoadingHourlySales(false);
        }
    };

    const loadHourlyLoginsData = async (organizationId) => {
        setLoadingHourlyLogins(true);
        setHourlyLoginsError('');

        try {
            const response = await apiService.getHourlySuccessfulLogins(organizationId);
            if (response.success) {
                setHourlyLoginsData(response.data || []);
                console.log('Hourly logins data loaded:', response.data);
            } else {
                setHourlyLoginsError(response.error || 'Failed to load hourly logins data');
                setHourlyLoginsData([]);
            }
        } catch (error) {
            console.error('Error loading hourly logins data:', error);
            setHourlyLoginsError('Failed to load hourly logins data');
            setHourlyLoginsData([]);
        } finally {
            setLoadingHourlyLogins(false);
        }
    };

    const loadDailySalesData = async (organizationId) => {
        setLoadingDailySales(true);
        setDailySalesError('');

        try {
            const response = await apiService.getDailySalesVolume(organizationId);
            if (response.success) {
                setDailySalesData(response.data || []);
                console.log('Daily sales data loaded:', response.data);
            } else {
                setDailySalesError(response.error || 'Failed to load daily sales data');
                setDailySalesData([]);
            }
        } catch (error) {
            console.error('Error loading daily sales data:', error);
            setDailySalesError('Failed to load daily sales data');
            setDailySalesData([]);
        } finally {
            setLoadingDailySales(false);
        }
    };

    const loadDailyLoginsData = async (organizationId) => {
        setLoadingDailyLogins(true);
        setDailyLoginsError('');

        try {
            const response = await apiService.getDailySuccessfulLogins(organizationId);
            if (response.success) {
                setDailyLoginsData(response.data || []);
                console.log('Daily logins data loaded:', response.data);
            } else {
                setDailyLoginsError(response.error || 'Failed to load daily logins data');
                setDailyLoginsData([]);
            }
        } catch (error) {
            console.error('Error loading daily logins data:', error);
            setDailyLoginsError('Failed to load daily logins data');
            setDailyLoginsData([]);
        } finally {
            setLoadingDailyLogins(false);
        }
    };

    const loadInventoryTransfersData = async (organizationId) => {
        setLoadingInventoryTransfers(true);
        setInventoryTransfersError('');

        try {
            const response = await apiService.getDailyStockTransferOrders(organizationId);
            if (response.success) {
                setInventoryTransfersData(response.data || []);
                console.log('Inventory transfers data loaded:', response.data);
            } else {
                setInventoryTransfersError(response.error || 'Failed to load inventory transfers data');
                setInventoryTransfersData([]);
            }
        } catch (error) {
            console.error('Error loading inventory transfers data:', error);
            setInventoryTransfersError('Failed to load inventory transfers data');
            setInventoryTransfersData([]);
        } finally {
            setLoadingInventoryTransfers(false);
        }
    };

    const loadUtilizationMetrics = async (organizationId) => {
        setLoadingUtilizationMetrics(true);
        setUtilizationMetricsError('');

        try {
            const response = await apiService.getUtilizationMetrics(organizationId);
            if (response.success) {
                setUtilizationMetrics(response.data || null);
                console.log('Utilization metrics loaded:', response.data);
            } else {
                setUtilizationMetricsError(response.error || 'Failed to load utilization metrics');
                setUtilizationMetrics(null);
            }
        } catch (error) {
            console.error('Error loading utilization metrics:', error);
            setUtilizationMetricsError('Failed to load utilization metrics');
            setUtilizationMetrics(null);
        } finally {
            setLoadingUtilizationMetrics(false);
        }
    };

    // A. Hourly Sales Volume (Last 24hrs)
    // Transform API data structure into displayable format
    const getHourlySalesDisplay = () => {
        if (hourlySalesData.length > 0 && hourlySalesData[0]) {
            const dataObj = hourlySalesData[0];
            const hourRanges = [];
            const volumes = [];

            // Extract hour ranges and volumes (only keys matching HH:00-HH:00 pattern)
            // This filters out metadata columns like organization_id, organization_code, etc.
            Object.keys(dataObj).forEach(key => {
                // Check if key matches hour range pattern (e.g., "00:00-01:00")
                if (key.match(/^\d{2}:\d{2}-\d{2}:\d{2}$/)) {
                    hourRanges.push(key);
                    volumes.push(dataObj[key]);
                }
            });

            return { hourRanges, volumes, total: dataObj.total || '0' };
        }

        // Default data if no API data
        const defaultHourRanges = Array.from({ length: 24 }, (_, i) => {
            const start = `${String(i).padStart(2, '0')}:00`;
            const end = `${String((i + 1) % 24).padStart(2, '0')}:00`;
            return `${start}-${end}`;
        });

        return {
            hourRanges: defaultHourRanges,
            volumes: Array(24).fill('0'),
            total: '0'
        };
    };

    const displayHourlySalesData = getHourlySalesDisplay();

    // B. Hourly Logins (Last 24hrs)
    const getHourlyLoginsDisplay = () => {
        if (hourlyLoginsData.length > 0 && hourlyLoginsData[0]) {
            const dataObj = hourlyLoginsData[0];
            const hourRanges = [];
            const volumes = [];

            // Extract hour ranges and volumes (only keys matching HH:00-HH:00 pattern)
            Object.keys(dataObj).forEach(key => {
                if (key.match(/^\d{2}:\d{2}-\d{2}:\d{2}$/)) {
                    hourRanges.push(key);
                    volumes.push(dataObj[key]);
                }
            });

            return { hourRanges, volumes, total: dataObj.total || '0' };
        }

        // Default data if no API data
        const defaultHourRanges = Array.from({ length: 24 }, (_, i) => {
            const start = `${String(i).padStart(2, '0')}:00`;
            const end = `${String((i + 1) % 24).padStart(2, '0')}:00`;
            return `${start}-${end}`;
        });

        return {
            hourRanges: defaultHourRanges,
            volumes: Array(24).fill('0'),
            total: '0'
        };
    };

    const displayHourlyLoginsData = getHourlyLoginsDisplay();

    // C. Daily Sales and Logins (Last 7 Days)
    const getDailyStatsDisplay = () => {
        const salesDataObj = dailySalesData.length > 0 && dailySalesData[0] ? dailySalesData[0] : {};
        const loginsDataObj = dailyLoginsData.length > 0 && dailyLoginsData[0] ? dailyLoginsData[0] : {};

        const dateMap = new Map();

        // Extract sales data
        Object.keys(salesDataObj).forEach(key => {
            if (key.match(/^[A-Z][a-z]{2} \d{2}$/)) {
                dateMap.set(key, {
                    date: key,
                    salesVolume: parseInt(salesDataObj[key]) || 0,
                    logins: 0
                });
            }
        });

        // Merge logins data
        Object.keys(loginsDataObj).forEach(key => {
            if (key.match(/^[A-Z][a-z]{2} \d{2}$/)) {
                if (dateMap.has(key)) {
                    dateMap.get(key).logins = parseInt(loginsDataObj[key]) || 0;
                } else {
                    dateMap.set(key, {
                        date: key,
                        salesVolume: 0,
                        logins: parseInt(loginsDataObj[key]) || 0
                    });
                }
            }
        });

        if (dateMap.size > 0) {
            // Convert map to array and sort by date
            const result = Array.from(dateMap.values());
            result.sort((a, b) => {
                const dateA = new Date(a.date + ', 2026');
                const dateB = new Date(b.date + ', 2026');
                return dateA - dateB;
            });
            return result;
        }

        return getDefaultDailyStats();
    };

    const getDefaultDailyStats = () => {
        // Default/fallback data if no API data
        return Array.from({ length: 7 }, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - (6 - i));
            return {
                date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                salesVolume: 0,
                logins: 0
            };
        });
    };

    const dailyStatsData = getDailyStatsDisplay();

    // D. Inventory Transfers by Type (Last 7 Days)
    const getInventoryTransfersDisplay = () => {
        if (inventoryTransfersData.length > 0) {
            // Group data by transfer_type
            const transfersByType = {};

            inventoryTransfersData.forEach(row => {
                const transferType = row.transfer_type || 'Unknown';
                if (!transfersByType[transferType]) {
                    transfersByType[transferType] = { transferType, dailyTransfers: [], total: 0 };
                }

                // Extract date keys and values (excluding metadata like transfer_type, total)
                const dailyData = [];
                let rowTotal = 0;
                Object.keys(row).forEach(key => {
                    if (key.match(/^[A-Z][a-z]{2} \d{2}$/)) {
                        dailyData.push({ date: key, count: parseInt(row[key]) || 0 });
                        rowTotal += parseInt(row[key]) || 0;
                    }
                });

                // Sort by date
                dailyData.sort((a, b) => {
                    const dateA = new Date(a.date + ', 2026');
                    const dateB = new Date(b.date + ', 2026');
                    return dateA - dateB;
                });

                transfersByType[transferType].dailyTransfers = dailyData.map(d => d.count);
                transfersByType[transferType].total = row.total !== undefined ? parseInt(row.total) : rowTotal;
            });

            return Object.values(transfersByType);
        }

        // Default/fallback data if no API data
        return [];
    };

    const processedInventoryTransfersData = getInventoryTransfersDisplay();

    // E. Staleness Data (Days Since Last Action)
    const stalenessData = [
        {
            metric: 'Sales',
            daysSinceLastAction: utilizationMetrics
                ? parseInt(utilizationMetrics.staleness_sales) || 0
                : 0
        },
        {
            metric: 'Successful Inventory Movement',
            daysSinceLastAction: utilizationMetrics
                ? parseInt(utilizationMetrics.staleness_stock_transfer_orders) || 0
                : 0
        }
    ];

    // F. Login Stats by Role
    const loginStatsByRole = [
        {
            role: 'Sales Rep',
            count: utilizationMetrics
                ? parseInt(utilizationMetrics.login_stats_sales_rep_weekly) || 0
                : 0,
            percentage: utilizationMetrics
                ? utilizationMetrics.login_stats_sales_rep_weekly_percentage_of_total || '0%'
                : '0%'
        },
        {
            role: 'Non-Sales Rep',
            count: utilizationMetrics
                ? parseInt(utilizationMetrics.login_stats_admin_weekly) || 0
                : 0,
            percentage: utilizationMetrics
                ? utilizationMetrics.login_stats_admin_weekly_percentage_of_total || '0%'
                : '0%'
        }
    ];
    const totalAccounts = utilizationMetrics
        ? parseInt(utilizationMetrics.total_users) || 0
        : 0;

    // H. User Engagement Stats
    const totalUserBase = utilizationMetrics
        ? parseInt(utilizationMetrics.total_users) || 0
        : 0;
    const dormantUsers = utilizationMetrics
        ? parseInt(utilizationMetrics.user_engagement_dormant_count) || 0
        : 0;
    const zeroSessionUsers = utilizationMetrics
        ? parseInt(utilizationMetrics.user_engagement_zero_session_count) || 0
        : 0;
    const dormantPercentage = utilizationMetrics
        ? utilizationMetrics.user_engagement_dormant_count_percentage_of_total || '0%'
        : '0%';
    const zeroSessionPercentage = utilizationMetrics
        ? utilizationMetrics.user_engagement_zero_session_count_percentage_of_total || '0%'
        : '0%';


    // Other Stats
    const loginsThisWeek = utilizationMetrics
        ? utilizationMetrics.logins_this_week || 0
        : 0;
    const loginsThisWeekVsLastWeek = utilizationMetrics
        ? utilizationMetrics.logins_this_week_vs_last_week || '0%'
        : '0%';
    const licenseUtilizationRatio = utilizationMetrics
        ? utilizationMetrics.license_utilization_ratio || '0/0'
        : '0/0';
    const licenseUtilizationPercentage = utilizationMetrics
        ? utilizationMetrics.license_utilization_percentage_ratio || '0%'
        : '0%';
    const engagementDecay = utilizationMetrics
        ? utilizationMetrics.engagement_decay_this_week || '0%'
        : '0%';
    const totalAccountsVsLastWeek = utilizationMetrics
        ? utilizationMetrics.total_users_vs_last_week || '0%'
        : '0%';
    const activeUserCount = utilizationMetrics
        ? parseInt(utilizationMetrics.user_engagement_active_user_count) || 0
        : 0;
    const activeUserPercentage = utilizationMetrics
        ? utilizationMetrics.user_engagement_active_user_count_percentage_of_total || '0%'
        : '0%';


    // Sales Reps and SSO Stats
    const salesRepCount = utilizationMetrics
        ? parseInt(utilizationMetrics.sales_rep_count) || 0
        : 0;
    const salesRepPercentage = utilizationMetrics
        ? utilizationMetrics.sales_rep_percentage_of_total || '0%'
        : '0%';
    const ssoCount = utilizationMetrics
        ? parseInt(utilizationMetrics.sso_count) || 0
        : 0;
    const ssoPercentage = utilizationMetrics
        ? utilizationMetrics.sso_percentage_of_total || '0%'
        : '0%';
    const totalUsers = activeUserCount + zeroSessionUsers + dormantUsers;

    const getStatusBadge = (days) => {
        if (days === 0) return <Badge bg="success">Active Today</Badge>;
        if (days <= 1) return <Badge bg="info">{days} day ago</Badge>;
        if (days <= 3) return <Badge bg="warning">{days} days ago</Badge>;
        return <Badge bg="danger">{days} days ago</Badge>;



    };

    return (
        <Container fluid className="py-4">
            {organizationError && (
                <Alert variant="danger" dismissible onClose={() => setOrganizationError('')}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {organizationError}
                </Alert>
            )}
            {hourlySalesError && (
                <Alert variant="danger" dismissible onClose={() => setHourlySalesError('')}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {hourlySalesError}
                </Alert>
            )}
            {hourlyLoginsError && (
                <Alert variant="danger" dismissible onClose={() => setHourlyLoginsError('')}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {hourlyLoginsError}
                </Alert>
            )}
            {dailySalesError && (
                <Alert variant="danger" dismissible onClose={() => setDailySalesError('')}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {dailySalesError}
                </Alert>
            )}
            {dailyLoginsError && (
                <Alert variant="danger" dismissible onClose={() => setDailyLoginsError('')}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {dailyLoginsError}
                </Alert>
            )}
            {inventoryTransfersError && (
                <Alert variant="danger" dismissible onClose={() => setInventoryTransfersError('')}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {inventoryTransfersError}
                </Alert>
            )}
            {utilizationMetricsError && (
                <Alert variant="danger" dismissible onClose={() => setUtilizationMetricsError('')}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {utilizationMetricsError}
                </Alert>
            )}

            {/* Customer Filter and Key Metrics in 40/60 Split */}
            <Row className="mb-4">
                {/* A. Customer Filter Section - 40% */}
                <Col md={5} lg={4}>
                    <Card className="h-100">
                        <Card.Body>
                            <Form.Group>
                                <Form.Label><strong>Filter by Organization</strong></Form.Label>
                                {loadingOrganizations ? (
                                    <div className="text-center py-2">
                                        <Spinner animation="border" size="sm" />
                                        <span className="ms-2">Loading organizations...</span>
                                    </div>
                                ) : (
                                    <Form.Select
                                        value={selectedCustomer}
                                        onChange={(e) => setSelectedCustomer(e.target.value)}
                                    >
                                        {organizations.map(org => (
                                            <option key={org.id} value={org.id}>
                                                {org.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                )}
                            </Form.Group>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Key Metrics Overview - 60% */}
                <Col md={7} lg={8}>
                    <Row className="g-3">
                        <Col sm={6} xl={3}>
                            <Card className="text-center h-100">
                                <Card.Body>
                                    <h6 className="text-muted">Users Logged in Last 7 Days</h6>
                                    <h2 className="mb-0">{loginsThisWeek}</h2>
                                    <small className="text-muted">{loginsThisWeekVsLastWeek}% vs previous 7 Days</small>
                                </Card.Body>
                            </Card>
                        </Col>
                        {/* <Col sm={6} xl={3}>
                            <Card className="text-center h-100">
                                <Card.Body>
                                    <h6 className="text-muted">Utilization vs License Ratio</h6>
                                    <h2 className="mb-0">{licenseUtilizationPercentage}</h2>
                                    <small className="text-muted">{licenseUtilizationRatio} licenses</small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col sm={6} xl={3}>
                            <Card className="text-center h-100">
                                <Card.Body>
                                    <h6 className="text-muted">Engagement Decay</h6>
                                    <h2 className={`mb-0 ${parseFloat(engagementDecay) < 0 ? 'text-danger' : 'text-success'}`}>
                                        {engagementDecay}
                                    </h2>
                                    <small className="text-muted">vs last week</small>
                                </Card.Body>
                            </Card>
                        </Col> */}
                        <Col sm={6} xl={3}>
                            <Card className="text-center h-100">
                                <Card.Body>
                                    <h6 className="text-muted">Total Active Accounts</h6>
                                    <h2 className="mb-0">{totalAccounts}</h2>
                                    <small className="text-muted">{totalAccountsVsLastWeek}% vs last week</small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col sm={6} xl={3}>
                            <Card className="text-center h-100">
                                <Card.Body>
                                    <h6 className="text-muted">Total Active Sales Reps</h6>
                                    <h2 className="mb-0">{salesRepCount}</h2>
                                    <small className="text-muted">{salesRepPercentage}% of total</small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col sm={6} xl={3}>
                            <Card className="text-center h-100">
                                <Card.Body>
                                    <h6 className="text-muted">Total Active SSOs</h6>
                                    <h2 className="mb-0">{ssoCount}</h2>
                                    <small className="text-muted">{ssoPercentage}% of total</small>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Col>
            </Row>

            {/* B. Hourly Statistics Table */}
            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header style={{ backgroundColor: '#e3f2fd', borderBottom: '2px solid #1976d2' }}>
                            <h5 className="mb-0" style={{ color: '#0d47a1' }}>
                                <i className="bi bi-clock me-2"></i>
                                Hourly Statistics (Last 24 Hours)
                                {(loadingHourlySales || loadingHourlyLogins) && (
                                    <Spinner animation="border" size="sm" className="ms-2" />
                                )}
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            {(loadingHourlySales || loadingHourlyLogins) ? (
                                <div className="text-center py-4">
                                    <Spinner animation="border" />
                                    <p className="mt-2">Loading hourly statistics data...</p>
                                </div>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <Table striped bordered hover size="sm">
                                        <thead>
                                            <tr>
                                                <th className="text-center" style={{ fontSize: '0.85em', minWidth: '80px' }}>
                                                    <strong>Metric</strong>
                                                </th>
                                                {displayHourlySalesData.hourRanges.map(hourRange => (
                                                    <th key={hourRange} className="text-center" style={{ fontSize: '0.85em', minWidth: '80px' }}>
                                                        {hourRange}
                                                    </th>
                                                ))}
                                                <th className="text-center bg-light" style={{ fontSize: '0.85em', minWidth: '80px' }}>
                                                    <strong>Total</strong>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td className="text-center">
                                                    <strong>Sales</strong>
                                                </td>
                                                {displayHourlySalesData.volumes.map((volume, index) => (
                                                    <td key={displayHourlySalesData.hourRanges[index]} className="text-center">
                                                        {volume}
                                                    </td>
                                                ))}
                                                <td className="text-center bg-light">
                                                    <strong>{displayHourlySalesData.total}</strong>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="text-center">
                                                    <strong>Logins</strong>
                                                </td>
                                                {displayHourlyLoginsData.volumes.map((volume, index) => (
                                                    <td key={displayHourlyLoginsData.hourRanges[index]} className="text-center">
                                                        {volume}
                                                    </td>
                                                ))}
                                                <td className="text-center bg-light">
                                                    <strong>{displayHourlyLoginsData.total}</strong>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* D. Inventory Transfers by Type */}
            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header style={{ backgroundColor: '#e0f2f1', borderBottom: '2px solid #00897b' }}>
                            <h5 className="mb-0" style={{ color: '#004d40' }}>
                                <i className="bi bi-box-seam me-2"></i>
                                Successful Inventory Transfers by Type (Last 7 Days)
                                {loadingInventoryTransfers && (
                                    <Spinner animation="border" size="sm" className="ms-2" />
                                )}
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>Transfer Type</th>
                                        {dailyStatsData.map((data, i) => (
                                            <th key={i} className="text-center">{data.date}</th>
                                        ))}
                                        <th className="text-end">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {processedInventoryTransfersData.length === 0 ? (
                                        <tr>
                                            <td colSpan={dailyStatsData.length + 2} className="text-center text-muted py-3">
                                                No Successful Transfers effected during the period
                                            </td>
                                        </tr>
                                    ) : (
                                        processedInventoryTransfersData.map((transfer, index) => (
                                            <tr key={index}>
                                                <td>{transfer.transferType}</td>
                                                {transfer.dailyTransfers.map((count, i) => (
                                                    <td key={i} className="text-center">{count}</td>
                                                ))}
                                                <td className="text-end fw-bold">{transfer.total}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* C & E. Activity Staleness and Daily Statistics Side by Side */}
            <Row className="mb-4">
                <Col md={5}>
                    <Card>
                        <Card.Header style={{ backgroundColor: '#f3e5f5', borderBottom: '2px solid #8e24aa' }}>
                            <h5 className="mb-0" style={{ color: '#4a148c' }}>
                                <i className="bi bi-hourglass-split me-2"></i>
                                Activity Staleness
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>Metric</th>
                                        <th className="text-end">Days Since Last Action</th>
                                        <th className="text-center">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stalenessData.map((data, index) => (
                                        <tr key={index}>
                                            <td>{data.metric}</td>
                                            <td className="text-end">{data.daysSinceLastAction}</td>
                                            <td className="text-center">{getStatusBadge(data.daysSinceLastAction)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={7}>
                    <Card>
                        <Card.Header style={{ backgroundColor: '#e8eaf6', borderBottom: '2px solid #5e35b1' }}>
                            <h5 className="mb-0" style={{ color: '#311b92' }}>
                                <i className="bi bi-calendar-week me-2"></i>
                                Daily Statistics (Last 7 Days)
                                {(loadingDailySales || loadingDailyLogins) && (
                                    <Spinner animation="border" size="sm" className="ms-2" />
                                )}
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <div style={{ overflowX: 'auto' }}>
                                <Table striped bordered hover size="sm">
                                    <thead>
                                        <tr>
                                            <th>Metric</th>
                                            {dailyStatsData.map((data, index) => (
                                                <th key={index} className="text-center" style={{ fontSize: '0.85em' }}>{data.date}</th>
                                            ))}
                                            <th className="text-end">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td><strong>Sales</strong></td>
                                            {dailyStatsData.map((data, index) => (
                                                <td key={index} className="text-center">{(data.salesVolume).toFixed(0)}</td>
                                            ))}
                                            <td className="text-end fw-bold">
                                                {(dailyStatsData.reduce((sum, d) => sum + d.salesVolume, 0)).toFixed(0)}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td><strong>Logins</strong></td>
                                            {dailyStatsData.map((data, index) => (
                                                <td key={index} className="text-center">{data.logins}</td>
                                            ))}
                                            <td className="text-end fw-bold">
                                                {dailyStatsData.reduce((sum, d) => sum + d.logins, 0)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* F & H. Login Stats by Role and User Engagement Metrics Side by Side */}
            <Row className="mb-4">
                <Col md={5}>
                    <Card>
                        <Card.Header style={{ backgroundColor: '#fff3e0', borderBottom: '2px solid #f57c00' }}>
                            <h5 className="mb-0" style={{ color: '#e65100' }}>
                                <i className="bi bi-people me-2"></i>
                                Login Stats by Role (last 7 Days)
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>Role</th>
                                        <th className="text-end">Count</th>
                                        <th className="text-end">Percentage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loginStatsByRole.map((stat, index) => (
                                        <tr key={index}>
                                            <td>{stat.role}</td>
                                            <td className="text-end">{stat.count}</td>
                                            <td className="text-end">{stat.percentage}% </td>
                                        </tr>
                                    ))}
                                    <tr className="table-secondary fw-bold">
                                        <td>Total Logins</td>
                                        <td className="text-end">{loginsThisWeek}</td>
                                        <td className="text-end">100%</td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={7}>
                    <Card>
                        <Card.Header style={{ backgroundColor: '#fce4ec', borderBottom: '2px solid #c2185b' }}>
                            <h5 className="mb-0" style={{ color: '#880e4f' }}>
                                <i className="bi bi-graph-down me-2"></i>
                                User Engagement Metrics
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>Metric</th>
                                        <th className="text-end">Count</th>
                                        <th className="text-end">Percentage of Total Users</th>
                                        <th className="text-end">Percentage of Total Accounts</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>

                                    <tr className="table-primary">
                                        <td><strong>Zero Session Users</strong></td>
                                        <td className="text-end">{zeroSessionUsers}</td>
                                        <td className="text-end">
                                            <Badge bg="danger">{(zeroSessionUsers / totalUsers * 100).toFixed(2)}%</Badge>
                                        </td>
                                        <td className="text-end">
                                            <Badge bg="danger">{zeroSessionPercentage}%</Badge>
                                        </td>
                                        <td>Never logged in</td>
                                    </tr>
                                    <tr className="table-warning">
                                        <td><strong>Dormant Users</strong></td>
                                        <td className="text-end">{dormantUsers}</td>
                                        <td className="text-end">
                                            <Badge bg="warning" style={{ color: 'black' }}>{(dormantUsers / totalUsers * 100).toFixed(2)}%</Badge>
                                        </td>
                                        <td className="text-end">
                                            <Badge bg="warning" style={{ color: 'black' }}>{dormantPercentage}%</Badge>
                                        </td>
                                        <td>No login in the last 7 days</td>
                                    </tr>
                                    <tr className="table-info">
                                        <td><strong>Active Users</strong></td>
                                        <td className="text-end">{activeUserCount}</td>
                                        <td className="text-end">
                                            <Badge bg="success">
                                                {(activeUserCount / totalUsers * 100).toFixed(2)}%
                                            </Badge>
                                        </td>
                                        <td className="text-end">
                                            <Badge bg="success">{activeUserPercentage}%</Badge>
                                        </td>
                                        <td>Logged in within the last 7 days</td>
                                    </tr>
                                    <tr className="table-secondary fw-bold">
                                        <td>Total Users*</td>
                                        <td className="text-end">{totalUsers}</td>
                                        <td className="text-end">100%</td>
                                        <td className="text-end">{(totalUsers / totalAccounts * 100).toFixed(2)}%</td>
                                        <td>*User: An account that can login (excludes SSOs)</td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default UtilizationStats;
